import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * How long visit recordings are kept before they are deleted.
 *
 * This is the only rule that actually guarantees deletion. A recording is removed when
 * a visit is finalized — but a visit that is never finalized keeps its audio for ever,
 * and some audio (upload chunks, protocol dictation) was never attached to a visit at
 * all. The sweep works by age across everything, so nothing is left behind because of
 * how it got there.
 *
 * The "due for deletion" figure is a live dry run, so the number always reflects what
 * the current setting would actually do.
 *
 * When a recording expires its note always stays a draft, and the clinic's admins are
 * notified. There is no longer an option to finalize automatically: that emailed the
 * patient AI-drafted content no clinician had read.
 */
export default function AudioRetention() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('3');
  const [unit, setUnit] = useState('days');
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/audio-retention`);
      setData(res.data);
      const h = res.data.retention_hours;
      if (h % 24 === 0) { setUnit('days'); setAmount(String(h / 24)); }
      else { setUnit('hours'); setAmount(String(h)); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load retention settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) { toast.error('Enter a whole number of hours or days'); return; }
    setSaving(true);
    try {
      const body = unit === 'days' ? { days: n } : { hours: n };
      const res = await axios.put(`${API_URL}/api/v1/superadmin/audio-retention`, body);
      toast.success(res.data?.message || 'Retention updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save retention');
    } finally {
      setSaving(false);
    }
  };

  const sweep = async (apply) => {
    setSweeping(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/v1/superadmin/audio-retention/sweep?apply=${apply ? 'true' : 'false'}`);
      toast.success(res.data?.message || 'Sweep complete');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  if (!data) {
    return (
      <Card sx={{ mt: 3 }}><CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={26} />
      </CardContent></Card>
    );
  }

  const due = data.on_disk?.due_for_deletion || 0;
  // "14, 12, 4 and 1" — a bare join reads as a list of unrelated numbers.
  const t = data.warning_thresholds || [14, 12, 4, 1];
  const thresholdList = t.length > 1
    ? `${t.slice(0, -1).join(', ')} and ${t[t.length - 1]}`
    : String(t[0]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>Recording retention</Typography>
          <Chip size="small" label={data.retention_label}
                color={data.is_default ? 'default' : 'primary'} />
          {data.is_default && <Chip size="small" variant="outlined" label="default" />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Visit recordings are deleted this long after they are made — whether or not the
          visit was ever finalized. The signed note is the record; the audio is not kept.
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
          <TextField
            label="Keep recordings for" type="number" size="small" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 1 }} sx={{ width: 170 }}
          />
          <TextField
            select label="Unit" size="small" value={unit}
            onChange={(e) => setUnit(e.target.value)} sx={{ width: 120 }}
          >
            <MenuItem value="hours">Hours</MenuItem>
            <MenuItem value="days">Days</MenuItem>
          </TextField>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ mt: 0.25 }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          When a recording expires
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Clinicians are warned {thresholdList} hours before it goes, and told again once
          it has.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          The recording is deleted and the note stays a draft for its clinician to finish
          and sign. Nothing is signed automatically and nothing is sent to the patient. The
          clinic&apos;s admins are notified so the unsigned visit is followed up.
        </Alert>

        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'action.hover', mb: 2, mt: 2 }}>
          <Typography variant="caption" color="text.secondary">ON DISK RIGHT NOW</Typography>
          <Stack direction="row" spacing={4} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{data.on_disk.files}</Typography>
              <Typography variant="caption" color="text.secondary">recordings</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>{data.on_disk.within_retention}</Typography>
              <Typography variant="caption" color="text.secondary">within retention</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}
                color={due ? 'warning.main' : 'text.primary'}>{due}</Typography>
              <Typography variant="caption" color="text.secondary">
                due for deletion ({data.on_disk.mb_due} MB)
              </Typography>
            </Box>
          </Stack>
        </Box>

        {(data.clinicians?.warnings_due_now > 0 || data.clinicians?.visits_expiring > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {data.clinicians.warnings_due_now > 0 && (
              <>{data.clinicians.warnings_due_now} clinician warning(s) are due. </>
            )}
            {data.clinicians.visits_expiring > 0 && (
              <>{data.clinicians.visits_expiring} unfinished visit(s) are past the period
                and will lose their recording on the next run.</>
            )}
          </Alert>
        )}

        {data.on_disk.unreferenced_due > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{data.on_disk.unreferenced_due}</strong> of those belong to no visit at
            all — upload fragments, or recordings left behind when a visit was closed.
            Nothing but this sweep will ever remove them.
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => sweep(false)} disabled={sweeping}>
            {sweeping ? 'Checking…' : 'Preview run'}
          </Button>
          <Button color="warning" variant="contained" onClick={() => sweep(true)}
                  disabled={sweeping || !due}>
            Delete {due} expired recording{due === 1 ? '' : 's'} now
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Running this also sends any warnings that are due. It normally runs hourly on a
          schedule. Use this to clear a backlog, or to check
          the setting does what you expect before trusting it to run unattended.
        </Typography>
      </CardContent>
    </Card>
  );
}
